import { useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useAccount } from '@/hooks/useAccount'
import { useFeatureFlags } from '@/lib/featureFlags'
import { setupTenantPushDeepLinks } from '@/lib/push/tenantPush'
import AppLayout from '@/components/AppLayout'
import PrivacyNoticeSheet from '@/components/PrivacyNoticeSheet'
import LeaseCodePage from '@/pages/LeaseCodePage'
import OtpPage from '@/pages/OtpPage'
import HomePage from '@/pages/HomePage'
import InvoicesPage from '@/pages/InvoicesPage'
import InvoiceDetailPage from '@/pages/InvoiceDetailPage'
import MaintenancePage from '@/pages/MaintenancePage'
import NewMaintenancePage from '@/pages/NewMaintenancePage'
import ParcelsPage from '@/pages/ParcelsPage'
import VehiclesPage from '@/pages/VehiclesPage'
import AnnouncementDetailPage from '@/pages/AnnouncementDetailPage'
import ErrorPage from '@/pages/ErrorPage'
import RewardsPage from '@/pages/RewardsPage'
import RewardQrPage from '@/pages/RewardQrPage'
import RewardCatalogPage from '@/pages/RewardCatalogPage'
import RewardHistoryPage from '@/pages/RewardHistoryPage'
import RetentionConsentPage from '@/pages/RetentionConsentPage'
import ExternalParkingPage from '@/pages/ExternalParkingPage'
import LprEventsPage from '@/pages/LprEventsPage'
import WinPickupPage from '@/pages/WinPickupPage'
import MeetingsPage from '@/pages/MeetingsPage'
import MeetingDetailPage from '@/pages/MeetingDetailPage'
import MyDataPage from '@/pages/MyDataPage'

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200">
      <div className="w-10 h-10 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
    </div>
  )
}

export default function App() {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { account, isLoading: accountLoading, error: accountError } = useAccount()
  const { loyaltyPoints, renewalBenefits } = useFeatureFlags()
  const navigate = useNavigate()

  // EP-156 T156-006: route a tapped invoice push to its detail page
  // (native-shell-guarded no-op in LINE).
  useEffect(() => {
    void setupTenantPushDeepLinks((path) => navigate(path))
  }, [navigate])

  if (accountLoading || authLoading) {
    return <LoadingScreen />
  }

  // LIFF ID not found - show error
  if (accountError || (!accountLoading && !account)) {
    return <ErrorPage />
  }

  return (
    <Routes>
      {/* Public routes — no auth, no LIFF session required */}
      <Route path="/parking/pay" element={<ExternalParkingPage />} />

      {/* Auth routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LeaseCodePage />}
      />
      <Route
        path="/otp"
        element={isAuthenticated ? <Navigate to="/" replace /> : <OtpPage />}
      />

      {/* Protected routes */}
      <Route
        element={
          isAuthenticated ? (
            <>
              <AppLayout />
              {/* EP-160 T160-103l: renders only while the tenant has not seen
                  the account's current privacy notice. */}
              <PrivacyNoticeSheet />
            </>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route path="/" element={<HomePage />} />
        <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="/maintenance/new" element={<NewMaintenancePage />} />
        <Route path="/parcels" element={<ParcelsPage />} />
        <Route path="/vehicles" element={<VehiclesPage />} />
        <Route path="/vehicles/events" element={<LprEventsPage />} />
        <Route path="/win-pickup" element={<WinPickupPage />} />
        {/* EP-135 Tier 3 — customer-facing meeting list + detail. Available to
            any authenticated person; an empty state covers tenant-only
            customers who never see a meeting. The detail page renders the
            ballot entry when the customer is entitled to cast (§12 S-4). */}
        <Route path="/meetings" element={<MeetingsPage />} />
        <Route path="/meetings/:id" element={<MeetingDetailPage />} />
        {/* EP-160 T160-104l (S6): the tenant's PDPA requests. LIFF has no
            settings page, so it is its own route, linked from HomePage. */}
        <Route path="/my-data" element={<MyDataPage />} />
        {/* Rewards (gated on loyalty_points flag) */}
        {loyaltyPoints && (
          <>
            <Route path="/rewards" element={<RewardsPage />} />
            <Route path="/rewards/qr" element={<RewardQrPage />} />
            <Route path="/rewards/catalog" element={<RewardCatalogPage />} />
            <Route path="/rewards/redemptions" element={<RewardHistoryPage />} />
          </>
        )}
        {/* Retention consent (gated on renewal_benefits flag) */}
        {renewalBenefits && (
          <Route path="/rewards/consent" element={<RetentionConsentPage />} />
        )}
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
